import { ExternalLink, FileText, Loader2, Pencil, Presentation, RefreshCw } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, useParams } from "react-router";

import { AppHeader } from "@/components/app-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type CaseContent,
  type CaseJourneyPhase,
  useCase,
  useRegenerateSlide,
  useUpdateCaseContent,
  useUpdateCaseStage,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import {
  cleanList,
  CountedTextarea,
  EditableList,
  EditableText,
  SLIDE_FIELD_MAX,
} from "./editable-section";
import { StageBadge } from "./stage-badge";

/** Renders a titled section; returns nothing when the body is empty. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="font-heading text-primary mb-3 text-2xl font-bold">{title}</h2>
      {children}
    </section>
  );
}

function hasText(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function hasItems<T>(value: T[] | null | undefined): value is T[] {
  return Array.isArray(value) && value.length > 0;
}

interface DefinitionRow {
  label: string;
  value: string | null | undefined;
}

function DefinitionList({ rows }: { rows: DefinitionRow[] }) {
  const visible = rows.filter((r) => hasText(r.value));
  if (visible.length === 0) {
    return null;
  }
  return (
    <dl className="divide-border divide-y">
      {visible.map((row) => (
        <div key={row.label} className="grid gap-1 py-3 sm:grid-cols-[220px_1fr] sm:gap-4">
          <dt className="text-muted text-sm font-medium">{row.label}</dt>
          <dd className="text-sm leading-6">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Keys of CaseContent that hold plain text edited via a textarea. */
type TextKey =
  | "tagline"
  | "about_client"
  | "challenge"
  | "results_commercial"
  | "results_speed"
  | "results_org"
  | "playbook"
  | "engagement_value"
  | "slide_sector"
  | "slide_title"
  | "slide_challenge"
  | "slide_strategy"
  | "slide_outcomes"
  | "slide_metric_1_value"
  | "slide_metric_1_label"
  | "slide_metric_2_value"
  | "slide_metric_2_label"
  | "image_prompt";

type ListKey = "why_boi" | "project_team" | "sources";

const TEXT_KEYS: TextKey[] = [
  "tagline",
  "about_client",
  "challenge",
  "results_commercial",
  "results_speed",
  "results_org",
  "playbook",
  "engagement_value",
  "slide_sector",
  "slide_title",
  "slide_challenge",
  "slide_strategy",
  "slide_outcomes",
  "slide_metric_1_value",
  "slide_metric_1_label",
  "slide_metric_2_value",
  "slide_metric_2_label",
  "image_prompt",
];

const LIST_KEYS: ListKey[] = ["why_boi", "project_team", "sources"];

const COUNTED_KEYS: TextKey[] = ["slide_challenge", "slide_strategy", "slide_outcomes"];

/** Editable working copy of the content while in edit mode. */
interface DraftContent {
  text: Record<TextKey, string>;
  lists: Record<ListKey, string[]>;
  journey: CaseJourneyPhase[];
}

function toDraft(content: Partial<CaseContent>): DraftContent {
  return {
    text: Object.fromEntries(TEXT_KEYS.map((k) => [k, content[k] ?? ""])) as Record<
      TextKey,
      string
    >,
    lists: Object.fromEntries(LIST_KEYS.map((k) => [k, [...(content[k] ?? [])]])) as Record<
      ListKey,
      string[]
    >,
    journey: (content.journey ?? []).map((p) => ({ ...p })),
  };
}

/** Builds the PATCH payload containing only the fields that changed. */
function diffDraft(draft: DraftContent, original: Partial<CaseContent>): Partial<CaseContent> {
  const changes: Partial<CaseContent> = {};
  for (const key of TEXT_KEYS) {
    const next = draft.text[key];
    const prev = original[key] ?? "";
    if (next !== prev) {
      changes[key] = next === "" ? null : next;
    }
  }
  for (const key of LIST_KEYS) {
    const next = cleanList(draft.lists[key]);
    const prev = original[key] ?? [];
    if (JSON.stringify(next) !== JSON.stringify(prev)) {
      changes[key] = next;
    }
  }
  const prevJourney = original.journey ?? [];
  if (JSON.stringify(draft.journey) !== JSON.stringify(prevJourney)) {
    changes.journey = draft.journey;
  }
  return changes;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const journeyCellInput =
  "w-full min-w-[7rem] rounded-md border border-transparent bg-transparent px-2 py-1 text-sm hover:border-border focus-visible:border-input focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none";

export function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const caseQuery = useCase(id);
  const stageMutation = useUpdateCaseStage();
  const contentMutation = useUpdateCaseContent(id);
  const regenerateMutation = useRegenerateSlide(id);
  const [tab, setTab] = useState("content");
  const [draft, setDraft] = useState<DraftContent | null>(null);

  const detail = caseQuery.data;
  const content: Partial<CaseContent> = detail?.content ?? {};
  const editing = draft !== null;

  const setText = (key: TextKey, value: string) =>
    setDraft((d) => (d ? { ...d, text: { ...d.text, [key]: value } } : d));
  const setList = (key: ListKey, items: string[]) =>
    setDraft((d) => (d ? { ...d, lists: { ...d.lists, [key]: items } } : d));
  const setJourneyCell = (idx: number, key: keyof CaseJourneyPhase, value: string) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            journey: d.journey.map((p, i) => (i === idx ? { ...p, [key]: value } : p)),
          }
        : d,
    );

  const overLimit = draft ? COUNTED_KEYS.filter((k) => draft.text[k].length > SLIDE_FIELD_MAX) : [];

  const startEditing = () => {
    contentMutation.reset();
    setDraft(toDraft(content));
  };

  const cancelEditing = () => {
    contentMutation.reset();
    setDraft(null);
  };

  const saveEditing = () => {
    if (!draft || overLimit.length > 0) {
      return;
    }
    const changes = diffDraft(draft, content);
    if (Object.keys(changes).length === 0) {
      setDraft(null);
      return;
    }
    logger.debug("[cases]", "saving content changes", Object.keys(changes));
    contentMutation.mutate(changes, {
      onSuccess: () => setDraft(null),
    });
  };

  const toggleStage = () => {
    if (!detail) {
      return;
    }
    stageMutation.mutate({
      caseId: detail.id,
      stage: detail.stage === "Approved" ? "Draft" : "Approved",
    });
  };

  const results: { title: string; key: TextKey; body: string | null | undefined }[] = [
    { title: "Commercial traction", key: "results_commercial", body: content.results_commercial },
    { title: "Speed of delivery", key: "results_speed", body: content.results_speed },
    { title: "Organisational impact", key: "results_org", body: content.results_org },
  ];
  const visibleResults = results.filter((r) => hasText(r.body));

  const slideRows: DefinitionRow[] = [
    { label: "Sector", value: content.slide_sector },
    { label: "Title", value: content.slide_title },
    { label: "Challenge", value: content.slide_challenge },
    { label: "Strategy", value: content.slide_strategy },
    { label: "Outcomes", value: content.slide_outcomes },
    {
      label: "Metric 1",
      value:
        hasText(content.slide_metric_1_value) || hasText(content.slide_metric_1_label)
          ? `${content.slide_metric_1_value ?? ""} — ${content.slide_metric_1_label ?? ""}`
          : null,
    },
    {
      label: "Metric 2",
      value:
        hasText(content.slide_metric_2_value) || hasText(content.slide_metric_2_label)
          ? `${content.slide_metric_2_value ?? ""} — ${content.slide_metric_2_label ?? ""}`
          : null,
    },
    { label: "NanaBanana image prompt", value: content.image_prompt },
  ];

  const slideEditRows: { label: string; key: TextKey; counted?: boolean; rows?: number }[] = [
    { label: "Sector", key: "slide_sector", rows: 1 },
    { label: "Title", key: "slide_title", rows: 1 },
    { label: "Challenge", key: "slide_challenge", counted: true },
    { label: "Strategy", key: "slide_strategy", counted: true },
    { label: "Outcomes", key: "slide_outcomes", counted: true },
    { label: "Metric 1 value", key: "slide_metric_1_value", rows: 1 },
    { label: "Metric 1 label", key: "slide_metric_1_label", rows: 1 },
    { label: "Metric 2 value", key: "slide_metric_2_value", rows: 1 },
    { label: "Metric 2 label", key: "slide_metric_2_label", rows: 1 },
    { label: "NanaBanana image prompt", key: "image_prompt", rows: 3 },
  ];

  return (
    <div className="bg-background min-h-screen">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-6 py-10">
        {caseQuery.isLoading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="text-muted h-6 w-6 animate-spin" />
          </div>
        ) : caseQuery.isError || !detail ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted text-sm">
                Could not load this case.{" "}
                <button
                  type="button"
                  onClick={() => void caseQuery.refetch()}
                  className="underline"
                >
                  Try again
                </button>
              </p>
            </CardContent>
          </Card>
        ) : (
          <article className="space-y-8">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/"
                  className="text-muted hover:text-primary text-sm underline-offset-4 hover:underline"
                >
                  ← Library
                </Link>
                <StageBadge stage={detail.stage} />
                <span className="text-muted text-sm">{detail.client_name}</span>
              </div>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <h1 className="font-heading text-primary max-w-2xl text-4xl font-bold text-balance">
                  {detail.slide_title}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                  {editing ? (
                    <>
                      <Button
                        onClick={saveEditing}
                        disabled={contentMutation.isPending || overLimit.length > 0}
                      >
                        {contentMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                        Save
                      </Button>
                      <Button
                        variant="outline"
                        onClick={cancelEditing}
                        disabled={contentMutation.isPending}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={startEditing}>
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant={detail.stage === "Approved" ? "outline" : "default"}
                        onClick={toggleStage}
                        disabled={stageMutation.isPending}
                      >
                        {stageMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                        {detail.stage === "Approved" ? "Unapprove" : "Approve"}
                      </Button>
                      {detail.slides_url && (
                        <a href={detail.slides_url} target="_blank" rel="noreferrer">
                          <Button variant="outline">
                            <Presentation className="h-4 w-4" />
                            Get slide
                          </Button>
                        </a>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => regenerateMutation.mutate()}
                        disabled={regenerateMutation.isPending}
                      >
                        {regenerateMutation.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Regenerating…
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4" />
                            Regenerate slide
                          </>
                        )}
                      </Button>
                      {detail.notion_page_url && (
                        <a href={detail.notion_page_url} target="_blank" rel="noreferrer">
                          <Button variant="ghost">
                            Notion <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                    </>
                  )}
                </div>
              </div>
              {hasText(detail.content_updated_at) && (
                <p className="text-muted text-xs">
                  Last edited
                  {hasText(detail.content_updated_by)
                    ? ` by ${detail.content_updated_by}`
                    : ""} · {formatDate(detail.content_updated_at)}
                </p>
              )}
              {contentMutation.isError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {contentMutation.error instanceof Error
                    ? contentMutation.error.message
                    : "Could not save changes."}
                </div>
              )}
              {editing && overLimit.length > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  Some slide fields are over the {SLIDE_FIELD_MAX}-character limit — shorten them
                  before saving.
                </div>
              )}
              {regenerateMutation.isError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {regenerateMutation.error instanceof Error
                    ? regenerateMutation.error.message
                    : "Could not regenerate the slide."}
                </div>
              )}
              {editing && draft ? (
                <div className="border-accent bg-accent-light/40 rounded-r-xl border-l-4 px-5 py-4">
                  <EditableText
                    value={draft.text.tagline}
                    onChange={(v) => setText("tagline", v)}
                    rows={2}
                    className="text-primary text-lg leading-7 font-medium"
                    aria-label="Tagline"
                  />
                </div>
              ) : (
                hasText(content.tagline) && (
                  <div className="border-accent bg-accent-light/40 rounded-r-xl border-l-4 px-5 py-4">
                    <p className="text-primary text-lg leading-7 font-medium">{content.tagline}</p>
                  </div>
                )
              )}
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="content">Case content</TabsTrigger>
                <TabsTrigger value="draft">Draft case</TabsTrigger>
              </TabsList>

              <TabsContent value="content" className="space-y-10 pt-8">
                {(editing || hasText(content.about_client)) && (
                  <Section title="About the client">
                    {editing && draft ? (
                      <EditableText
                        value={draft.text.about_client}
                        onChange={(v) => setText("about_client", v)}
                        aria-label="About the client"
                      />
                    ) : (
                      <p className="text-sm leading-7">{content.about_client}</p>
                    )}
                  </Section>
                )}

                {(editing || hasText(content.challenge)) && (
                  <Section title="The challenge">
                    {editing && draft ? (
                      <EditableText
                        value={draft.text.challenge}
                        onChange={(v) => setText("challenge", v)}
                        aria-label="The challenge"
                      />
                    ) : (
                      <p className="text-sm leading-7">{content.challenge}</p>
                    )}
                  </Section>
                )}

                {(editing || hasItems(content.why_boi)) && (
                  <Section title="Why they chose BOI">
                    {editing && draft ? (
                      <>
                        <EditableList
                          items={draft.lists.why_boi}
                          onChange={(items) => setList("why_boi", items)}
                          aria-label="Why they chose BOI"
                        />
                        <p className="text-muted mt-1 text-xs">One reason per line.</p>
                      </>
                    ) : (
                      <ul className="list-disc space-y-2 pl-5 text-sm leading-6">
                        {(content.why_boi ?? []).map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    )}
                  </Section>
                )}

                {(editing ? draft && draft.journey.length > 0 : hasItems(content.journey)) && (
                  <Section title="The journey">
                    <div className="border-border overflow-x-auto rounded-xl border">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-border bg-primary/5 border-b text-left">
                            <th className="text-primary px-4 py-3 font-semibold">Phase</th>
                            <th className="text-primary px-4 py-3 font-semibold">Period</th>
                            <th className="text-primary px-4 py-3 font-semibold">What we did</th>
                            <th className="text-primary px-4 py-3 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-border divide-y">
                          {editing && draft
                            ? draft.journey.map((phase, idx) => (
                                <tr key={idx}>
                                  <td className="px-2 py-2 align-top font-medium">
                                    <input
                                      value={phase.phase ?? ""}
                                      onChange={(e) => setJourneyCell(idx, "phase", e.target.value)}
                                      className={journeyCellInput}
                                      aria-label={`Journey phase ${idx + 1} name`}
                                    />
                                  </td>
                                  <td className="px-2 py-2 align-top">
                                    <input
                                      value={phase.period ?? ""}
                                      onChange={(e) =>
                                        setJourneyCell(idx, "period", e.target.value)
                                      }
                                      className={cn(journeyCellInput, "text-muted")}
                                      aria-label={`Journey phase ${idx + 1} period`}
                                    />
                                  </td>
                                  <td className="px-2 py-2 align-top">
                                    <textarea
                                      value={phase.what_we_did ?? ""}
                                      onChange={(e) =>
                                        setJourneyCell(idx, "what_we_did", e.target.value)
                                      }
                                      rows={2}
                                      className={cn(journeyCellInput, "min-w-[14rem] leading-6")}
                                      aria-label={`Journey phase ${idx + 1} what we did`}
                                    />
                                  </td>
                                  <td className="px-2 py-2 align-top">
                                    <input
                                      value={phase.status ?? ""}
                                      onChange={(e) =>
                                        setJourneyCell(idx, "status", e.target.value)
                                      }
                                      className={cn(journeyCellInput, "text-muted")}
                                      aria-label={`Journey phase ${idx + 1} status`}
                                    />
                                  </td>
                                </tr>
                              ))
                            : (content.journey ?? []).map((phase, idx) => (
                                <tr key={`${phase.phase ?? ""}-${idx}`}>
                                  <td className="px-4 py-3 font-medium whitespace-nowrap">
                                    {phase.phase}
                                  </td>
                                  <td className="text-muted px-4 py-3 whitespace-nowrap">
                                    {phase.period}
                                  </td>
                                  <td className="px-4 py-3 leading-6">{phase.what_we_did}</td>
                                  <td className="text-muted px-4 py-3 whitespace-nowrap">
                                    {phase.status}
                                  </td>
                                </tr>
                              ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>
                )}

                {(editing || visibleResults.length > 0) && (
                  <Section title="Results & impact">
                    <div className="grid gap-4 md:grid-cols-3">
                      {(editing ? results : visibleResults).map((r) => (
                        <Card key={r.title}>
                          <CardContent className="p-5">
                            <h3 className="font-heading text-primary text-base font-bold">
                              {r.title}
                            </h3>
                            {editing && draft ? (
                              <EditableText
                                value={draft.text[r.key]}
                                onChange={(v) => setText(r.key, v)}
                                rows={4}
                                className="text-muted mt-2 leading-6"
                                aria-label={r.title}
                              />
                            ) : (
                              <p className="text-muted mt-2 text-sm leading-6">{r.body}</p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </Section>
                )}

                {(editing || hasText(content.playbook)) && (
                  <Section title="How we worked">
                    {editing && draft ? (
                      <EditableText
                        value={draft.text.playbook}
                        onChange={(v) => setText("playbook", v)}
                        aria-label="How we worked"
                      />
                    ) : (
                      <p className="text-sm leading-7">{content.playbook}</p>
                    )}
                  </Section>
                )}

                {(editing || hasText(content.engagement_value)) && (
                  <Section title="Engagement value">
                    {editing && draft ? (
                      <EditableText
                        value={draft.text.engagement_value}
                        onChange={(v) => setText("engagement_value", v)}
                        aria-label="Engagement value"
                      />
                    ) : (
                      <p className="text-sm leading-7">{content.engagement_value}</p>
                    )}
                  </Section>
                )}

                {(editing || slideRows.some((r) => hasText(r.value))) && (
                  <Section title="Slide fields">
                    {editing && draft ? (
                      <dl className="divide-border divide-y">
                        {slideEditRows.map((row) => (
                          <div
                            key={row.key}
                            className="grid gap-1 py-3 sm:grid-cols-[220px_1fr] sm:gap-4"
                          >
                            <dt className="text-muted pt-1 text-sm font-medium">{row.label}</dt>
                            <dd className="text-sm">
                              {row.counted ? (
                                <CountedTextarea
                                  value={draft.text[row.key]}
                                  onChange={(v) => setText(row.key, v)}
                                  rows={3}
                                  className="leading-6"
                                  aria-label={row.label}
                                />
                              ) : (
                                <EditableText
                                  value={draft.text[row.key]}
                                  onChange={(v) => setText(row.key, v)}
                                  rows={row.rows ?? 2}
                                  className="min-h-0 leading-6"
                                  aria-label={row.label}
                                />
                              )}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <DefinitionList rows={slideRows} />
                    )}
                  </Section>
                )}

                {(editing || hasItems(content.project_team)) && (
                  <Section title="Project team">
                    {editing && draft ? (
                      <>
                        <EditableList
                          items={draft.lists.project_team}
                          onChange={(items) => setList("project_team", items)}
                          aria-label="Project team"
                        />
                        <p className="text-muted mt-1 text-xs">One person per line.</p>
                      </>
                    ) : (
                      <ul className="list-disc space-y-1 pl-5 text-sm leading-6">
                        {(content.project_team ?? []).map((member) => (
                          <li key={member}>{member}</li>
                        ))}
                      </ul>
                    )}
                  </Section>
                )}

                {(editing || hasItems(content.sources)) && (
                  <Section title="Sources">
                    {editing && draft ? (
                      <>
                        <EditableList
                          items={draft.lists.sources}
                          onChange={(items) => setList("sources", items)}
                          aria-label="Sources"
                        />
                        <p className="text-muted mt-1 text-xs">One URL per line.</p>
                      </>
                    ) : (
                      <ul className="list-disc space-y-1 pl-5 text-sm leading-6">
                        {(content.sources ?? []).map((source) => (
                          <li key={source}>
                            <a
                              href={source}
                              target="_blank"
                              rel="noreferrer"
                              className="text-accent break-all underline-offset-4 hover:underline"
                            >
                              {source}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Section>
                )}
              </TabsContent>

              <TabsContent value="draft" className="pt-8">
                <Card>
                  <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
                    <FileText className="text-muted h-8 w-8" />
                    <p className="font-heading text-primary text-lg font-bold">
                      Draft case coming soon
                    </p>
                    <p className="text-muted max-w-md text-sm leading-6">
                      A publish-ready draft article for this case will appear here in a future
                      version. The structure is intentionally ready for it.
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </article>
        )}
      </main>
    </div>
  );
}
